import { useState } from 'react';
import { CalendarDays, Repeat } from 'lucide-react';
import {
  TASK_REPEAT_HOUR,
  TASK_REPEAT_MAX_EVERY,
  TASK_REPEAT_WEEK_ORDER,
  describeTaskRepeat,
  nextTaskRepeatAt,
  parseTaskRepeat,
  type TaskRepeat,
  type TaskRepeatUnit,
} from '@shared/task-repeat';
import { formatCalendarDay, parseCalendarDay, weekday, type CalendarDay } from '@shared/wall-clock';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { NumberInput } from '@/components/NumberInput';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InfoTooltip } from '@/components/InfoTooltip';
import { formatDay } from '@/lib/formatters';
import { projectToday } from '@/lib/snooze';
import { cn } from '@/lib/utils';
import { repeatNextLabel, repeatZone } from './task-repeat-label';

const hourLabel = `${String(TASK_REPEAT_HOUR).padStart(2, '0')}:00`;
const UNIT_NAMES: Record<TaskRepeatUnit, [string, string]> = { day: ['day', 'days'], week: ['week', 'weeks'], month: ['month', 'months'] };
const WEEKDAY_LETTERS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

/** "Mon 5 Oct" for a calendar day, whatever this browser's timezone. */
function calendarDayLabel(day: CalendarDay): string {
  return formatDay(formatCalendarDay(day), { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * Set, change or stop a task's Repeat: every n days, weeks (on chosen days) or months (on
 * a day of the month), from a start day. It comes back at 08:00 project time.
 */
export function TaskRepeatDialog({
  open,
  onOpenChange,
  repeat,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  repeat: TaskRepeat | null;
  /** The new Repeat and when it first comes back, or null to stop it. */
  onSave: (repeat: TaskRepeat | null, nextAt: Date | null) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Mounted only while open, so each opening starts from the task's Repeat. */}
        <RepeatForm repeat={repeat} onCancel={() => onOpenChange(false)} onSave={onSave} />
      </DialogContent>
    </Dialog>
  );
}

function RepeatForm({
  repeat,
  onCancel,
  onSave,
}: {
  repeat: TaskRepeat | null;
  onCancel: () => void;
  onSave: (repeat: TaskRepeat | null, nextAt: Date | null) => void;
}) {
  const [now] = useState(() => new Date());
  const [draft, setDraft] = useState(() => {
    const today = projectToday(now, repeatZone());
    return {
      every: repeat?.every ?? 1,
      unit: repeat?.unit ?? ('week' as TaskRepeatUnit),
      weekdays: repeat?.weekdays ?? [weekday(today)],
      month_day: repeat?.month_day ?? Math.min(today.day, 28),
      start: repeat?.start ?? formatCalendarDay(today),
      new_task: repeat?.new_task ?? false,
    };
  });
  const [picking, setPicking] = useState(false);

  const every = draft.every;
  const parsed = parseTaskRepeat(draft);
  const nextAt = parsed.ok ? nextTaskRepeatAt(parsed.repeat, now, repeatZone()) : null;
  const startDay = parseCalendarDay(draft.start)!;
  const local = (d: { year: number; month: number; day: number }) => new Date(d.year, d.month - 1, d.day);

  const toggleDay = (day: number) =>
    setDraft((d) => ({ ...d, weekdays: d.weekdays.includes(day) ? d.weekdays.filter((x) => x !== day) : [...d.weekdays, day] }));

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          Repeat
          <InfoTooltip>
            Each time it comes round, the task goes back to the top of the first column at {hourLabel}, and its followers are
            notified. If it’s still open, it stays where it is.
          </InfoTooltip>
        </DialogTitle>
        <DialogDescription>{parsed.ok ? describeTaskRepeat(parsed.repeat) : parsed.error}</DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm">
          <span className="w-16 shrink-0 text-muted-foreground">Every</span>
          <NumberInput
            min={1}
            max={TASK_REPEAT_MAX_EVERY[draft.unit]}
            integer
            allowEmpty={false}
            value={draft.every}
            onValueChange={(next) => setDraft((d) => ({ ...d, every: next ?? d.every }))}
            aria-label="Every how many"
            className="h-9 w-20"
          />
          <Select value={draft.unit} onValueChange={(unit) => setDraft((d) => ({ ...d, unit: unit as TaskRepeatUnit }))}>
            <SelectTrigger className="w-32" aria-label="Days, weeks or months">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(UNIT_NAMES) as TaskRepeatUnit[]).map((unit) => (
                <SelectItem key={unit} value={unit}>
                  {every === 1 ? UNIT_NAMES[unit][0] : UNIT_NAMES[unit][1]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {draft.unit === 'week' ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="w-16 shrink-0 text-muted-foreground">On</span>
            {/* Smaller and closer on phones, so all seven days fit the screen (T-35). */}
            <div className="flex gap-0.5 sm:gap-1">
              {TASK_REPEAT_WEEK_ORDER.map((day) => {
                const on = draft.weekdays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={on}
                    onClick={() => toggleDay(day)}
                    className={cn(
                      'size-8 shrink-0 rounded-full border text-xs font-medium transition-colors sm:size-9',
                      on ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'
                    )}
                  >
                    {WEEKDAY_LETTERS[day]}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {draft.unit === 'month' ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="w-16 shrink-0 text-muted-foreground">On day</span>
            <Select value={String(draft.month_day)} onValueChange={(v) => setDraft((d) => ({ ...d, month_day: Number(v) }))}>
              <SelectTrigger className="w-32" aria-label="Day of the month">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <SelectItem key={day} value={String(day)}>
                    {day}
                  </SelectItem>
                ))}
                <SelectItem value="-1">Last day</SelectItem>
              </SelectContent>
            </Select>
            {draft.month_day > 28 ? (
              <InfoTooltip>In a month without this day, it comes back on the month’s last day.</InfoTooltip>
            ) : null}
          </div>
        ) : null}

        <div className="flex items-center gap-2 text-sm">
          <span className="w-16 shrink-0 text-muted-foreground">Starting</span>
          <Popover open={picking} onOpenChange={setPicking}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="font-normal">
                <CalendarDays className="text-muted-foreground" />
                {calendarDayLabel(startDay)}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={local(startDay)}
                defaultMonth={local(startDay)}
                onSelect={(day) => {
                  if (!day) return;
                  setDraft((d) => ({ ...d, start: formatCalendarDay({ year: day.getFullYear(), month: day.getMonth() + 1, day: day.getDate() }) }));
                  setPicking(false);
                }}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <Checkbox
            id="repeat-new-task"
            checked={draft.new_task}
            onCheckedChange={(checked) => setDraft((d) => ({ ...d, new_task: checked === true }))}
          />
          <Label htmlFor="repeat-new-task" className="font-normal">
            Make a new task each time
          </Label>
          <InfoTooltip>
            When it comes round, a copy of this task (title, description, people, labels and links, not the chat) goes on
            top of the first column, and the Repeat moves to it: change or stop it on the newest one. Off: this same task
            comes back. Either way, if it’s still open, nothing new is made.
          </InfoTooltip>
        </div>

        <p className="rounded-md bg-muted px-3 py-2 text-sm">
          {nextAt ? (
            <>
              Next back <span className="font-medium">{repeatNextLabel(nextAt)}</span>
            </>
          ) : (
            <span className="text-muted-foreground">Finish the Repeat to see when it comes back.</span>
          )}
        </p>
      </div>

      <DialogFooter className="gap-2 sm:justify-between">
        {repeat ? (
          <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={() => onSave(null, null)}>
            Stop repeating
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2 max-sm:w-full max-sm:[&>*]:flex-1">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={!parsed.ok || !nextAt} onClick={() => parsed.ok && onSave(parsed.repeat, nextAt)}>
            <Repeat />
            {repeat ? 'Save' : 'Repeat'}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
